/* eslint-disable camelcase */
import React from "react";
import BlogPost from "components/blog/post";
import BlogLayout from "layouts/BlogLayout";
import { NextPage } from "next";
import { Crumb } from "components/Breadcrumb";
import { getContentfulEntries } from "services/contentful/client/contentfulClient";
import { IPostFields } from "type-definitions/generated/contentful";
import { EntryCollection } from "contentful";
import { transformContentfulPost } from "pages/blogzoeken/index.page";
import { BlogPostType } from "components/blog/BlogTypes";
import { ArticleStructuredData } from "components/StructuredData";
import { reroute } from "helpers/reroute";
import { getContentfulFields } from "lib/contentful";

const getBreadcrumbTrail = (): Crumb[] => {
    return [
        {
            title: "Home",
            mask: "/",
            url: "/",
            external: true
        },
        {
            title: "Blog",
            mask: "/blog",
            url: "/blog"
        }
    ];
};

const getCanonicalUrl = (
    data?: BlogPostType,
    categorySlug?: string,
    slug?: string
): string | undefined => {
    if (data) {
        if (data.canonicalUrl) {
            return data.canonicalUrl;
        }
        return `https://www.autotrack.nl/blog/${categorySlug}/${slug}`;
    }
    return undefined;
};

type Props = {
    slug?: string;
    post?: EntryCollection<IPostFields>;
    watIsPosts?: EntryCollection<IPostFields>;
    aanradersPosts?: EntryCollection<IPostFields>;
};

const Post: NextPage<Props> = (props) => {
    const { slug, post, aanradersPosts, watIsPosts } = props;
    const breadcrumbTrail = getBreadcrumbTrail();
    let categoryName = "Advies";
    let categorySlug = "advies";

    const items = post?.items[0];

    if (!items) {
        reroute();
        return null;
    }

    const postWithDynamicContentList = {
        ...post,
        items: [
            {
                ...post.items[0],
                fields: {
                    ...post.items[0].fields,
                    dynamicContentList: getContentfulFields(post)?.fields.dynamicContentList
                }
            }
        ]
    };

    const transformedPost = postWithDynamicContentList?.items.map(transformContentfulPost)[0];
    const transformedAanradersPosts = aanradersPosts?.items.map(transformContentfulPost);
    const transformedWatIsPosts = watIsPosts?.items.map(transformContentfulPost);

    let content = null;

    if (!transformedPost) {
        content = <p>Loading...</p>;
    } else {
        categoryName =
            transformedPost.categories && transformedPost.categories.length
                ? transformedPost.categories[0].name
                : "Advies";

        categorySlug =
            transformedPost.categories && transformedPost.categories.length
                ? transformedPost.categories[0].slug
                : "advies";

        // Add category
        breadcrumbTrail.push({
            title: transformedPost.categories[0].name,
            mask: `/blog/${transformedPost.categories[0].slug}`,
            url: `/blogzoeken?category=${transformedPost.categories[0].slug}`
        });

        // Add active post title
        breadcrumbTrail.push({
            title: transformedPost.title,

            active: true
        });

        content = (
            <BlogPost
                post={transformedPost}
                whatIsPosts={transformedWatIsPosts ?? []}
                recommendedPosts={transformedAanradersPosts ?? []}
            />
        );
    }

    return (
        <>
            <BlogLayout
                metaDescription={transformedPost ? transformedPost.metaDescription : undefined}
                canonicalUrl={getCanonicalUrl(transformedPost, categorySlug, slug)}
                title={transformedPost ? transformedPost.title : "Loading"}
                description={transformedPost ? transformedPost.title : "Loading"}
                socialMediaImage={transformedPost ? `https:${transformedPost.imageSrc}` : undefined}
                headerTitle={categoryName}
                mainClass="news"
                category={categoryName}
                searchCategory={categorySlug}
                breadcrumbTrail={breadcrumbTrail}
                pageName="blog-post"
                pageType="blogPost"
            >
                {content}
            </BlogLayout>
            {transformedPost && (
                <ArticleStructuredData
                    headline={transformedPost.title}
                    image={`https:${transformedPost.imageSrc}`}
                    datePublished={transformedPost.publishDate}
                    author={transformedPost?.author?.display_name}
                />
            )}
        </>
    );
};

Post.getInitialProps = async ({ query, res }): Promise<any> => {
    const slug = Array.isArray(query.slug) ? query.slug[0] : query.slug;

    const { category } = query ?? {};
    const categorySlugUrl = `/blog/${category}/${slug}`;

    if (res && categorySlugUrl === "/blog/over-autotrack/over-autotrack") {
        // @ts-ignore
        res.redirect(308, "/over-autotrack");
    }

    const postBySlugParams = {
        "contentType": "post",
        "fields.slug": slug ?? "",
        "limit": 4,
        "include": 3
    };
    const watIsParams = {
        "contentType": "post",
        "include": 3,
        "select": "sys.id,fields",
        "fields.categories.sys.id[in]": "3Q4fLF1b9C7n14Dk7aoU3H", // get all the posts with the wat-is category
        "limit": 5,
        "order": "-fields.created_at" // sort by this field
    };
    const aanradersParams = {
        "contentType": "post",
        "include": 3,
        "select": "sys.id,fields",
        "fields.categories.sys.id[in]": "66DsBJ1o1zaTSbDxS1Xx42", // get all the posts with the aanraders category
        "limit": 5,
        "order": "-fields.created_at" // sort by this field
    };

    try {
        const post = await getContentfulEntries(postBySlugParams);
        const watIsPosts = await getContentfulEntries(watIsParams);
        const aanradersPosts = await getContentfulEntries(aanradersParams);
        if (!post) {
            const url = ["blog", ...Object.keys(query).map((k) => query[k])];
            reroute(res, url);
        }
        return {
            post,
            watIsPosts,
            aanradersPosts,
            slug
        };
    } catch (error: any) {
        const contentfulError = {
            errorType: "CONTENTFUL_FETCH_ERROR",
            errorLog: error.message
        };
        console.error(JSON.stringify(contentfulError));

        return {
            post: undefined,
            watIsPosts: undefined,
            aanradersPosts: undefined,
            slug
        };
    }
};

Post.displayName = "Post";

export default Post;
